import keycloakClient from "@keycloak/keycloak";
import { BACKEND_API_URL } from "../../config";
import {
  BlackboardMastersResponse,
  BlackboardUserResponse
} from "./apiResponseModels";
import { ChatRequestPayload, ChatResponse, SummaryResponse } from "./models";

class ChatManagerApi {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeaders(includeContentType: boolean = true): HeadersInit {
    const headers: HeadersInit = {
      Authorization: `Bearer ${keycloakClient.token}`,
      Accept: "application/json"
    };
    if (includeContentType) {
      headers["Content-Type"] = "application/json";
    }
    return headers;
  }

  private handleApiError(error: any): never {
    console.error("Error en la comunicación con el Chat Manager API:", error);
    throw error;
  }

  private prepareFormData(payload: ChatRequestPayload): FormData {
    const { session_id, user_input, user_voice_record, language } = payload;

    const default_language = "spanish";

    const formData = new FormData();
    formData.append("session_id", session_id);
    formData.append("language", default_language);

    if (user_input) {
      formData.append("user_input", user_input);
    }

    if (user_voice_record) {
      formData.append(
        "user_voice_record",
        user_voice_record,
        "voice-recording.mp3"
      );
    }

    return formData;
  }

  async sendChatMessage(payload: ChatRequestPayload): Promise<ChatResponse> {
    try {
      const formData = this.prepareFormData(payload);

      const response = await fetch(`${this.baseUrl}/chat`, {
        method: "POST",
        headers: this.getAuthHeaders(false),
        body: formData
      });

      if (!response.ok) {
        throw new Error(
          `Error en la petición: ${response.status} ${response.statusText}`
        );
      }

      return (await response.json()) as ChatResponse;
    } catch (error) {
      this.handleApiError(error);
    }
  }

  async sendChatRequestWithAudioOrText(
    sessionId: string,
    language: string,
    text?: string,
    audioBlob?: Blob | null
  ): Promise<ChatResponse> {
    const default_language = "spanish";
    try {
      const payload: ChatRequestPayload = {
        session_id: sessionId,
        language: default_language,
        ...(audioBlob && { user_voice_record: audioBlob }),
        ...(text && { user_input: text })
      };
      return await this.sendChatMessage(payload);
    } catch (error) {
      console.error("Error sending chat request:", error);
      return { response: "" };
    }
  }

  async generateSummary(
    sessionId: string,
    language: string
  ): Promise<SummaryResponse> {
    try {
      const response = await fetch(
        `${this.baseUrl}/summary/${sessionId}?language=spanish`,
        {
          method: "GET",
          headers: this.getAuthHeaders(true)
        }
      );

      if (!response.ok) {
        throw new Error(
          `Error al solicitar resumen: ${response.status} ${response.statusText}`
        );
      }

      return (await response.json()) as SummaryResponse;
    } catch (error) {
      return this.handleApiError(error);
    }
  }

  public async validateBlackboardUserInfo(
    email: string,
    token: string
  ): Promise<BlackboardUserResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/users?email=${email}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json"
        }
      });

      if (response.status === 404) {
        throw new Error("User not found in Blackboard.");
      }

      if (!response.ok) {
        throw new Error(
          `Error validating user: ${response.status} ${response.statusText}`
        );
      }

      return (await response.json()) as BlackboardUserResponse;
    } catch (error) {
      return this.handleApiError(error);
    }
  }

  public async getBlackboardUserMasters(
    userName: string,
    token: string
  ): Promise<BlackboardMastersResponse> {
    try {
      const response = await fetch(
        `${this.baseUrl}/users/${userName}/masters`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json"
          }
        }
      );

      if (!response.ok) {
        // No consideramos un error si no tiene masters, simplemente devolvemos un array vacío.
        if (response.status === 404) {
          return { results: [] };
        }
        throw new Error(
          `Error getting masters: ${response.status} ${response.statusText}`
        );
      }

      return (await response.json()) as BlackboardMastersResponse;
    } catch (error) {
      return this.handleApiError(error);
    }
  }
}

const PREFIX = "cesif-api/v1";

export const chatManagerApiService = new ChatManagerApi(
  `${BACKEND_API_URL}/${PREFIX}`
);
